import {
  ArrowBendDownRight,
  ChatCircleText,
  Prohibit,
  Star,
  Warning,
} from "@phosphor-icons/react"

import { Badge } from "@/components/ui/badge"
import { Card } from "@/components/ui/card"
import { Empty } from "@/components/ui/empty"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"

import { formatRelative } from "./helpers"
import { StarRating } from "./star-rating"
import type { DashboardDraft, DashboardReview } from "./types"

// A review starts pending (needs a reply) and ends up resolved — either a
// response was posted, or the draft was dismissed without posting. Reviews
// never expire — on Google you can reply anytime — so an unaddressed review
// just stays pending rather than dropping into a graveyard.
//
// Selecting a pending row opens the focus modal to approve/dismiss; selecting
// a resolved row opens the read-only view.
export function InboxPanel({
  drafts,
  reviews,
  onSelectDraft,
  onSelectResolved,
}: {
  drafts: DashboardDraft[]
  reviews: DashboardReview[]
  onSelectDraft: (uid: string) => void
  onSelectResolved: (uid: string) => void
}) {
  return (
    <Card className="gap-0 rounded-xl py-0 shadow-none">
      <div className="px-5 pt-4 pb-3">
        <h2 className="text-sm font-medium">Reviews</h2>
      </div>
      <Tabs defaultValue="pending" className="gap-0">
        <div className="border-b px-5">
          <TabsList variant="line" className="gap-6 px-0">
            <TabsTrigger value="pending" className="flex-none px-0">
              Pending
              <Badge variant="draft" className="border-0">
                {drafts.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="resolved" className="flex-none px-0">
              Resolved
              <Badge variant="draft" className="border-0">
                {reviews.length}
              </Badge>
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent
          value="pending"
          className={drafts.length === 0 ? "" : "min-h-[376px]"}
        >
          {drafts.length === 0 ? (
            <Empty
              icon={<ChatCircleText className="size-5" />}
              title="Nothing waiting"
              description="Drafts that need your approval will appear here."
              className="px-5"
            />
          ) : (
            <div className="divide-y">
              {drafts.map((draft) => (
                <SelectableDraftRow
                  key={draft.uid}
                  draft={draft}
                  onSelect={() => onSelectDraft(draft.uid)}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent
          value="resolved"
          className={reviews.length === 0 ? "" : "min-h-[376px]"}
        >
          {reviews.length === 0 ? (
            <Empty
              icon={<Star className="size-5" />}
              title="Nothing resolved yet"
              description="Reviews you've responded to or dismissed will appear here."
              className="px-5"
            />
          ) : (
            <div className="divide-y">
              {reviews.map((review) => (
                <ReviewRow
                  key={review.uid}
                  review={review}
                  onSelect={() => onSelectResolved(review.uid)}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </Card>
  )
}

// A pending row — shows the review (the thing to triage); the drafted response
// lives in the modal. Clicking opens the modal to approve/dismiss.
function SelectableDraftRow({
  draft,
  onSelect,
}: {
  draft: DashboardDraft
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      className="hover:bg-muted/40 w-full px-5 py-4 text-left transition-colors"
      onClick={onSelect}
    >
      <div className="mb-1 flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <p className="truncate text-sm font-semibold">
            {draft.reviewer_name}
          </p>
          <StarRating rating={draft.rating} />
          {draft.flagged && (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300">
              <Warning className="size-3" weight="fill" />
              {draft.flag_reason ?? "Flagged"}
            </span>
          )}
        </div>
        <p className="text-muted-foreground shrink-0 text-xs">
          {formatRelative(draft.created_at)}
        </p>
      </div>
      <p className="text-muted-foreground line-clamp-1 text-sm leading-snug">
        {draft.review_body ?? draft.draft_excerpt}
      </p>
    </button>
  )
}

function ReviewRow({
  review,
  onSelect,
}: {
  review: DashboardReview
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      className="hover:bg-muted/40 w-full px-5 py-4 text-left transition-colors"
      onClick={onSelect}
    >
      <div className="mb-1 flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <p className="truncate text-sm font-semibold">
            {review.reviewer_name}
          </p>
          <StarRating rating={review.rating} />
        </div>
        <p className="text-muted-foreground shrink-0 text-xs">
          {formatRelative(review.received_at)}
        </p>
      </div>
      <p className="text-muted-foreground line-clamp-1 text-sm leading-snug">
        {review.body}
      </p>
      {review.response ? (
        <div className="mt-1 flex items-start gap-1.5">
          <ArrowBendDownRight className="text-muted-foreground mt-0.5 size-3.5 shrink-0" />
          <p className="line-clamp-1 text-sm leading-snug">{review.response}</p>
        </div>
      ) : review.status === "dismissed" ? (
        <div className="text-muted-foreground mt-1 flex items-center gap-1.5 text-sm italic">
          <Prohibit className="size-3.5 shrink-0" />
          <span>Dismissed without a response</span>
        </div>
      ) : null}
    </button>
  )
}
